import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useRouter } from '@tanstack/react-router';
import { Layers } from 'lucide-react';
import { z } from 'zod';
import { buttonVariants } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCount, formatTaka } from '@/utils/number';
import { pcmpUrgentBatchesQueryOptions } from '../../api/pcmpRequests.api';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { useStagePcmpUrgentBatch } from '../../hooks/usePcmpUrgentBatchActions';
import type { PcmpUrgentStageResult, PcmpUrgentStageSelection } from '../../types/pcmpUrgent.types';
import { PcmpOutcomeDialog } from '../PcmpBatch/PcmpOutcomeDialog';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const NEW_BATCH = '__new__';

const stageFormSchema = z.object({
  batch: z.string(),
  comment: z.string().trim().max(PCMP_MKT_COMMENT_MAX),
});

type StageFormValues = z.infer<typeof stageFormSchema>;

export interface PcmpUrgentStageTarget {
  count: number;
  amount: number;
  selection: PcmpUrgentStageSelection;
}

interface PcmpUrgentStageDialogProps {
  target: PcmpUrgentStageTarget;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  /** Called once proposals were staged, so the caller can clear its selection. */
  onStaged: () => void;
  onClose: () => void;
}

/**
 * Legacy urgent Stage: put Pre-Batch proposals into a new urgent batch, or add
 * them to one that is still open. The batch stays staged until it is published.
 */
export function PcmpUrgentStageDialog({
  target,
  refNoOf,
  onStaged,
  onClose,
}: PcmpUrgentStageDialogProps) {
  const router = useRouter();
  const [outcome, setOutcome] = useState<PcmpUrgentStageResult | null>(null);
  const { data: batches } = useQuery(pcmpUrgentBatchesQueryOptions({ page: 1, limit: 100 }));
  const form = useForm<StageFormValues>({
    resolver: zodResolver(stageFormSchema),
    defaultValues: { batch: NEW_BATCH, comment: '' },
  });

  const openBatch = (batchNo: number) =>
    void router.navigate({ to: '/pcmp/requests/batches/$batchNo', params: { batchNo } });

  const { mutate, isPending, error } = useStagePcmpUrgentBatch((result) => {
    onStaged();
    if (result.skipped.length > 0) setOutcome(result);
    else openBatch(result.batchNo);
  });

  // A staged batch is open until it is published, even when rollbacks emptied it.
  const openBatches = (batches?.batches.items ?? []).filter((batch) => batch.status === 'STAGED');
  const batchItems = [
    { value: NEW_BATCH, label: 'New batch' },
    ...openBatches.map((batch) => ({
      value: String(batch.batchNo),
      label: `#${batch.batchNo} · ${formatCount(batch.wholeBatchItemCount, 'proposal')}`,
    })),
  ];

  const onSubmit = form.handleSubmit((values) =>
    mutate({
      batchNo: values.batch === NEW_BATCH ? undefined : Number(values.batch),
      comment: values.comment === '' ? undefined : values.comment,
      ...target.selection,
    }),
  );

  if (outcome) {
    return (
      <PcmpOutcomeDialog
        title={`Urgent batch #${outcome.batchNo} ${outcome.isNewBatch ? 'staged' : 'updated'}`}
        summary={`${formatCount(outcome.staged.length, 'proposal')} staged. The ones below changed since the list was loaded and were left out.`}
        skipped={outcome.skipped}
        refNoOf={refNoOf}
        actions={
          <Link
            to="/pcmp/requests/batches/$batchNo"
            params={{ batchNo: outcome.batchNo }}
            className={buttonVariants()}
          >
            Open batch #{outcome.batchNo}
          </Link>
        }
        onClose={onClose}
      />
    );
  }

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Stage Urgent Batch"
        description="Accounts urgent payment"
        icon={Layers}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Stage"
        pendingLabel="Staging..."
      >
        <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <span className="text-xs text-muted-foreground">Accounts pays</span>
          <span className="font-medium">
            {formatCount(target.count, 'proposal')} · {formatTaka(target.amount)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          The batch stays staged until it is published. Proposals that changed meanwhile (held, no
          longer in Pre-Batch) are left out and listed.
        </p>

        <FormField
          control={form.control}
          name="batch"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Batch</FormLabel>
              <Select<string>
                items={batchItems}
                value={field.value}
                onValueChange={(next) => next !== null && field.onChange(next)}
              >
                <FormControl>
                  <SelectTrigger aria-label="Batch" className="w-full">
                    <SelectValue />
                    <SelectIcon />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {batchItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>Add to an open batch, or start a new one.</FormDescription>
            </FormItem>
          )}
        />

        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          placeholder="Optional note for the timeline"
          description="Recorded on each proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
